import { Wallet } from 'ethers';
import React, { useState } from 'react';
import styled from 'styled-components';

import SpinningRoundedBorder from '#/components/basic/animations/SpinningRoundedBorder';
import Button from '#/components/basic/Button/Button';
import { KATANA_PERPS } from '#/config/constants';
import { makeEthersErrorHumanRedable } from '#/lib/ethereum/ethersErrors';
import { associateWallet } from '#/lib/ethereum/wallets/assignWallets';
import { waitAsync } from '#/lib/promiseTools';
import QrCodeScanner from '#/lib/qrCode/QrCodeScanner';
import { setDelegatedKey } from '#/reduxState/delegatedKey/authorizationReducer';
import { staticDispatch } from '#/reduxState/store';
import {
  IWallet,
  changeSoftwareWallet,
  removeWallet,
} from '#/reduxState/wallet/walletReducer';

import { parseSyncQrCompressedNumsPayload } from '../qrSyncPayload';

import QrSyncDescriptionMobile from './QrSyncDescriptionMobile';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LoadingWrap = styled.div`
  text-align: center;
  font-size: var(--medium2);
`;

const Top = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 8px;
`;

const Bottom = styled.div`
  flex: 1;
  flex-grow: 0;
  padding: 4px 24px 24px 24px;
  border-top: 1px solid var(--separator);
`;

const ScannerWrap = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

export default function QrSyncMobile({
  remember,
  onUnlock,
  onError,
}: {
  remember: boolean;
  onUnlock: () => void;
  onError: (error: string) => void;
}) {
  const [consent, setConsent] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const handleAssociateScanned = async (qrResult: string) => {
    const qrData = parseSyncQrCompressedNumsPayload(qrResult);
    console.log('[QrSync] Custody addr', qrData.custodyWalletAddress);

    setUnlocking(true);
    staticDispatch(removeWallet());

    const custodyWallet: IWallet = {
      walletAddress: qrData.custodyWalletAddress.toLowerCase(),
      walletAddressOriginal: qrData.custodyWalletAddress,
      walletType: 'qrSynced',
    };

    const hdWallet = new Wallet(qrData.delegatedPrivateKey);

    const delegatedKeyAddress = hdWallet.address;
    console.log('[QrSync] DK Address', delegatedKeyAddress);

    const { delegatedKey } = await associateWallet(custodyWallet, true, {
      delegatedKeyParams: {
        delegatedHDWallet: hdWallet,
        delegatedKey: delegatedKeyAddress,
        delegatedNonce: qrData.nonce,
        delegatedName: `${KATANA_PERPS} Connect`,
      },
      nonce: qrData.nonce,
      signature: qrData.authorizationSignature,
    });

    if (!delegatedKey) {
      throw new Error('Cannot associate wallet DK');
    }
    console.log('[QrSync] Associate success');

    // Give QRCodePlugin time to unsubscribed
    await waitAsync(500);

    staticDispatch(
      changeSoftwareWallet({
        walletType: 'qrSynced',
        wallet: qrData.custodyWalletAddress,
      })
    );
    staticDispatch(
      setDelegatedKey(remember ? 'persistent' : 'session', delegatedKey)
    );
    onUnlock();
  };
  return (
    <Wrap>
      <Top>
        {!consent ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <h2>QR code scanning requires camera access.</h2>
            <Button onClick={() => setConsent(true)}>
              Request Camera Access
            </Button>
          </div>
        ) : unlocking ? (
          <LoadingWrap>
            <SpinningRoundedBorder />
            <p>Connecting...</p>
          </LoadingWrap>
        ) : (
          <ScannerWrap>
            <QrCodeScanner
              successCallback={result => {
                handleAssociateScanned(result).catch(error => {
                  console.warn(error);
                  onError(makeEthersErrorHumanRedable(error));
                });
              }}
              errorCallback={onError}
            />
          </ScannerWrap>
        )}
      </Top>
      <Bottom>
        <QrSyncDescriptionMobile />
      </Bottom>
    </Wrap>
  );
}
